import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import {
  ArrowLeft,
  Calculator,
  FileDown,
  FileSpreadsheet,
  Lock,
  Unlock,
  AlertTriangle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/ui/date-picker";
import { Card } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

import { useDailyPayments } from "@/hooks/arqueo/useDailyPayments";
import { useDailyClosure } from "@/hooks/arqueo/useDailyClosure";
import { usePaymentMethods } from "@/hooks/payments/usePaymentMethods";
import { usePaymentCategories } from "@/hooks/payments/usePaymentCategories";
import { useGymArticles } from "@/hooks/payments/useGymArticles";
import {
  ArqueoPayment,
  ArticleInfo,
  computeTotals,
  formatYmd,
  getPaymentTimeMinutes,
  ARTICLES_CATEGORY_LABEL,
} from "@/lib/arqueo/aggregations";
import { exportArqueoExcel, exportArqueoPdf } from "@/lib/arqueo/exporters";
import { ArqueoFilters, ArqueoFilterState } from "@/components/admin/arqueo/ArqueoFilters";
import { ArqueoTable } from "@/components/admin/arqueo/ArqueoTable";
import { ArqueoSummary } from "@/components/admin/arqueo/ArqueoSummary";

const DEFAULT_FILTERS: ArqueoFilterState = {
  method: "all",
  category: "all",
  articleId: "all",
  startTime: "",
  endTime: "",
  sortBy: "time_desc",
};

const ArqueoCaja = () => {
  const [searchParams] = useSearchParams();
  const paramGymId = searchParams.get("gym_id");
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();

  const { data: managedGymId } = useQuery({
    queryKey: ["managedGymId-arqueo", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gym_managers")
        .select("gym_id")
        .eq("manager_id", user!.id)
        .limit(1)
        .maybeSingle();
      if (error) return null;
      return data?.gym_id || null;
    },
    enabled: !!user && !paramGymId && !profile?.gym_id,
  });

  const gymId = paramGymId || profile?.gym_id || managedGymId || null;

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [filters, setFilters] = useState<ArqueoFilterState>(DEFAULT_FILTERS);

  const { data: gym } = useQuery({
    queryKey: ["gym-arqueo", gymId],
    queryFn: async () => {
      if (!gymId) return null;
      const { data } = await supabase
        .from("gyms")
        .select("name")
        .eq("id", gymId)
        .single();
      return data;
    },
    enabled: !!gymId,
  });

  const { data: payments, isLoading } = useDailyPayments({ gymId, date: selectedDate });
  const { closure, closeDay } = useDailyClosure(gymId, selectedDate);
  const { methods } = usePaymentMethods(gymId);
  const { categories } = usePaymentCategories(gymId);
  const { articles } = useGymArticles(gymId);

  const articlesById = useMemo(() => {
    const m = new Map<string, ArticleInfo>();
    for (const a of articles) m.set(a.id, { id: a.id, name: a.name, price: a.price });
    return m;
  }, [articles]);

  const dayPayments: ArqueoPayment[] = payments || [];

  const filteredPayments = useMemo(() => {
    const filtered = dayPayments.filter((p) => {
      if (filters.method !== "all") {
        if ((p.payment_method || "") !== filters.method) return false;
      }
      if (filters.category !== "all") {
        if (filters.category === ARTICLES_CATEGORY_LABEL) {
          if (!p.is_article_sale) return false;
          if (filters.articleId !== "all") {
            if ((p.article_id || "") !== filters.articleId) return false;
          }
        } else {
          if (p.is_article_sale) return false;
          const cats = (p.description || "").split(",").map((s) => s.trim());
          if (!cats.includes(filters.category)) return false;
        }
      }
      if (filters.startTime || filters.endTime) {
        const mins = getPaymentTimeMinutes(p);
        if (mins == null) return false;
        if (filters.startTime) {
          const [h, m] = filters.startTime.split(":").map(Number);
          if (mins < h * 60 + m) return false;
        }
        if (filters.endTime) {
          const [h, m] = filters.endTime.split(":").map(Number);
          if (mins > h * 60 + m) return false;
        }
      }
      return true;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      switch (filters.sortBy) {
        case "time_asc": {
          const am = getPaymentTimeMinutes(a);
          const bm = getPaymentTimeMinutes(b);
          if (am == null && bm == null) return 0;
          if (am == null) return 1;
          if (bm == null) return -1;
          return am - bm;
        }
        case "amount_desc":
          return (Number(b.amount) || 0) - (Number(a.amount) || 0);
        case "amount_asc":
          return (Number(a.amount) || 0) - (Number(b.amount) || 0);
        case "time_desc":
        default: {
          const am = getPaymentTimeMinutes(a);
          const bm = getPaymentTimeMinutes(b);
          if (am == null && bm == null) return 0;
          if (am == null) return 1;
          if (bm == null) return -1;
          return bm - am;
        }
      }
    });
    return sorted;
  }, [dayPayments, filters]);

  const filteredTotals = useMemo(
    () => computeTotals(filteredPayments, articlesById),
    [filteredPayments, articlesById]
  );
  const fullDayTotals = useMemo(
    () => computeTotals(dayPayments, articlesById),
    [dayPayments, articlesById]
  );

  const isPartial =
    filters.method !== "all" ||
    filters.category !== "all" ||
    !!filters.startTime ||
    !!filters.endTime;

  const isClosed = !!closure;
  const isFutureDate = formatYmd(selectedDate) > formatYmd(new Date());

  const handleClearFilters = () => setFilters(DEFAULT_FILTERS);

  const handleExportPdf = () => {
    exportArqueoPdf({
      payments: filteredPayments,
      totals: filteredTotals,
      articlesById,
      date: selectedDate,
      gymName: gym?.name || "Gimnasio",
      isPartial,
      isClosed,
    });
  };

  const handleExportExcel = () => {
    exportArqueoExcel({
      payments: filteredPayments,
      totals: filteredTotals,
      articlesById,
      date: selectedDate,
      gymName: gym?.name || "Gimnasio",
      isPartial,
      isClosed,
    });
  };

  const handleCloseDay = () => {
    closeDay.mutate({
      payments: dayPayments,
      totals: fullDayTotals,
    });
  };

  const summaryRef = useRef<HTMLDivElement>(null);
  const [summaryHeight, setSummaryHeight] = useState(160);

  useEffect(() => {
    const el = summaryRef.current;
    if (!el) return;
    const update = () => setSummaryHeight(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [filteredTotals]);

  return (
    <div
      className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background text-primary pt-16 px-3 sm:px-4 lg:px-6"
      style={{ paddingBottom: summaryHeight + 32 }}
    >
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header */}
        <div>
          <Button
            variant="ghost"
            onClick={() =>
              navigate(gymId ? `/total-pagos?gym_id=${gymId}` : "/total-pagos")
            }
            className="text-primary/70 hover:text-primary hover:bg-primary/10 -ml-2 mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Volver
          </Button>

          <div className="flex items-start gap-4 flex-wrap">
            <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/10">
              <Calculator className="h-8 w-8 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-primary">
                Arqueo de Caja
              </h1>
              <p className="text-primary/60 mt-1 text-sm sm:text-base">
                {gym?.name ? `${gym.name} — ` : ""}
                Cierre diario y desglose de pagos del día
              </p>
            </div>
          </div>
        </div>

        {/* Día */}
        <section>
          <h2 className="text-base sm:text-lg font-semibold text-primary mb-2">Día</h2>
          <Card className="p-3 sm:p-4 bg-card/60 backdrop-blur border-primary/10">
            <div className="flex flex-wrap items-end gap-3 sm:gap-4">
              <div className="flex flex-col gap-1 w-full sm:w-auto">
                <label className="text-xs text-primary/70">Fecha</label>
                <div className="w-full sm:w-48">
                  <DatePicker
                    value={formatYmd(selectedDate)}
                    onChange={(v) => {
                      if (!v) return;
                      const [y, m, d] = v.split("-").map(Number);
                      if (y && m && d) setSelectedDate(new Date(y, m - 1, d));
                    }}
                    maxYear={new Date().getFullYear()}
                  />
                </div>
                <div className="text-xs text-primary/60 mt-1">
                  {format(selectedDate, "EEEE d 'de' MMMM yyyy", { locale: es })}
                </div>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs text-primary/70">Estado</label>
                {isClosed ? (
                  <Badge className="bg-red-500/15 text-red-400 border border-red-500/30 hover:bg-red-500/15">
                    <Lock className="h-3 w-3 mr-1" /> CERRADO
                  </Badge>
                ) : (
                  <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/15">
                    <Unlock className="h-3 w-3 mr-1" /> ABIERTO
                  </Badge>
                )}
              </div>
            </div>

            {isClosed && (
              <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm text-red-300 flex items-center gap-2">
                <Lock className="h-4 w-4" />
                <span>
                  Día cerrado el{" "}
                  {format(new Date(closure!.closed_at), "dd/MM/yyyy HH:mm")}.
                  El arqueo es de solo lectura.
                </span>
              </div>
            )}
          </Card>
        </section>

        {/* Descargas */}
        <section>
          <h2 className="text-base sm:text-lg font-semibold text-primary mb-2">Descargas</h2>
          <Card className="p-3 sm:p-4 bg-card/60 backdrop-blur border-primary/10">
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={handleExportPdf}
                disabled={filteredPayments.length === 0}
                className="flex-1 sm:flex-none min-w-0"
              >
                <FileDown className="mr-2 h-4 w-4" /> PDF
              </Button>
              <Button
                variant="outline"
                onClick={handleExportExcel}
                disabled={filteredPayments.length === 0}
                className="flex-1 sm:flex-none min-w-0"
              >
                <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
              </Button>

              {!isClosed && !isFutureDate && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
                      disabled={dayPayments.length === 0 || closeDay.isPending}
                    >
                      <Lock className="mr-2 h-4 w-4" /> Cerrar día
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-black/90 border-primary/20 text-primary">
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Cerrar el arqueo del día?</AlertDialogTitle>
                      <AlertDialogDescription className="text-primary/70">
                        Se registrará el cierre con los totales del día completo
                        ({dayPayments.length} pagos, total $
                        {fullDayTotals.total.toLocaleString("es-AR")}). Esta
                        acción dejará el arqueo como solo lectura.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleCloseDay}>
                        Cerrar día
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </Card>
        </section>

        {/* Filtros */}
        <section>
          <h2 className="text-base sm:text-lg font-semibold text-primary mb-2">Filtros</h2>
          <ArqueoFilters
            filters={filters}
            onChange={setFilters}
            onClear={handleClearFilters}
            methods={methods.map((m) => m.name)}
            categories={categories.map((c) => c.name)}
            articles={articles.map((a) => ({ id: a.id, name: a.name }))}
          />
        </section>

        {isPartial && (
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-sm text-amber-300 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Vista parcial — los totales reflejan los filtros aplicados.
          </div>
        )}

        {/* Table */}
        <ArqueoTable
          payments={filteredPayments}
          articlesById={articlesById}
          isLoading={isLoading}
        />

        {/* Sticky Summary */}
        <ArqueoSummary ref={summaryRef} totals={filteredTotals} isPartial={isPartial} />
      </div>
    </div>
  );
};

export default ArqueoCaja;
